import { useState } from 'react';
import { saveCountry, updateCountry } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import ToggleField from '../../shared/Forms/ToggleField';
import Button from '../../shared/UI/Button';

export default function CountryForm({ country, onSaved, onCancel }) {
  const { user } = useAuth();
  const [form, setForm] = useState(country ? { ...country } : {
    id: '', name: '', currency_code: '', currency_name: '',
    currency_symbol: '', legal_framework: '', region: '', is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validate() {
    const e = {};
    if (!form.name?.trim()) e.name = 'Country name is required';
    if (!form.id?.trim()) e.id = 'ISO code is required';
    if (form.id?.trim().length !== 2) e.id = 'ISO code must be exactly 2 characters';
    if (!form.currency_code?.trim()) e.currency_code = 'Currency code is required';
    if (form.currency_code?.trim().length !== 3) e.currency_code = 'Currency code must be exactly 3 characters';
    if (!form.currency_name?.trim()) e.currency_name = 'Currency name is required';
    if (!form.currency_symbol?.trim()) e.currency_symbol = 'Currency symbol is required';
    if (!form.region?.trim()) e.region = 'Region is required';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const data = {
        ...form,
        id: form.id.toUpperCase(),
        currency_code: form.currency_code.toUpperCase(),
      };
      if (country) {
        await updateCountry({ countryId: country.id, updates: data, performedBy: user.uid });
      } else {
        await saveCountry({ countryData: data, performedBy: user.uid });
      }
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onSaved(); }, 1800);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {success && <div className="alert alert-success">Country saved successfully!</div>}

      <div className="form-grid mb-4">
        <FormField label="Country Name" required error={errors.name}>
          <input className={`form-input ${errors.name ? 'error' : ''}`}
            value={form.name} onChange={e => set('name', e.target.value)} />
        </FormField>
        <FormField label="ISO Code (2 chars)" required error={errors.id}>
          <input className={`form-input ${errors.id ? 'error' : ''}`}
            value={form.id} onChange={e => set('id', e.target.value.toUpperCase())}
            maxLength={2} placeholder="e.g. GH"
            readOnly={!!country} style={country ? { background: 'var(--color-border-light)', cursor: 'not-allowed' } : {}} />
        </FormField>
        <FormField label="Currency Code (ISO 4217)" required error={errors.currency_code}>
          <input className={`form-input ${errors.currency_code ? 'error' : ''}`}
            value={form.currency_code} maxLength={3} placeholder="e.g. GHS"
            onChange={e => set('currency_code', e.target.value.toUpperCase())} />
        </FormField>
        <FormField label="Currency Name" required error={errors.currency_name}>
          <input className={`form-input ${errors.currency_name ? 'error' : ''}`}
            value={form.currency_name} onChange={e => set('currency_name', e.target.value)}
            placeholder="e.g. Ghanaian Cedi" />
        </FormField>
        <FormField label="Currency Symbol" required error={errors.currency_symbol}>
          <input className={`form-input ${errors.currency_symbol ? 'error' : ''}`}
            value={form.currency_symbol} onChange={e => set('currency_symbol', e.target.value)}
            placeholder="e.g. ₵" />
        </FormField>
        <SelectField label="Legal Framework" value={form.legal_framework}
          onChange={e => set('legal_framework', e.target.value)}
          options={[
            { value: 'common_law', label: 'Common Law' },
            { value: 'civil_law', label: 'Civil Law' },
            { value: 'mixed', label: 'Mixed' },
          ]} />
        <FormField label="Region" required error={errors.region}>
          <input className={`form-input ${errors.region ? 'error' : ''}`}
            value={form.region} onChange={e => set('region', e.target.value)}
            placeholder="e.g. West Africa" />
        </FormField>
        <ToggleField label="Is Active" checked={form.is_active !== false}
          onChange={v => set('is_active', v)} />
      </div>

      <div className="flex-between">
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}>
          {country ? 'Save Changes' : 'Add Country'}
        </Button>
      </div>
    </form>
  );
}
